import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { TECH_TABS } from "../../data/marketing";

const statusTone = (status) => {
  const value = status.toLowerCase();
  if (value.includes("compliant") || value.includes("verified") || value.includes("completed") || value.includes("scheduled")) return "text-emerald-700 bg-emerald-500/10";
  if (value.includes("input") || value.includes("due")) return "text-amber-800 bg-amber-500/15";
  return "text-forest-deep/70 bg-forest-deep/5";
};

export default function TechPreview({ compact = false }) {
  const [active, setActive] = useState("epr");
  const tab = TECH_TABS[active];

  return (
    <div className="overflow-hidden rounded-3xl border border-forest-deep/10 bg-white shadow-xl shadow-forest-deep/10" data-testid="tech-preview">
      <div className="border-b border-forest-deep/10 bg-forest-deep px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300">CLYMACO client technology</div>
            <div className="mt-1 font-display text-lg font-bold text-[#FBF9F4]">Your programme. One connected view.</div>
          </div>
          <div className="flex rounded-full bg-white/10 p-1" role="tablist" aria-label="Technology preview views">
            {Object.entries(TECH_TABS).map(([key, item]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active === key}
                onClick={() => setActive(key)}
                className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${active === key ? "bg-emerald-400 text-forest-deep" : "text-emerald-100/70 hover:text-white"}`}
                data-testid={`tech-preview-tab-${key}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.24 }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-forest-deep/60" data-testid="tech-preview-title">{tab.title}</h3>
              <span className="rounded-full bg-amber-500/15 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-800">Interface concept · Example data</span>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {tab.metrics.map((metric, index) => (
                <motion.div key={metric.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="rounded-2xl bg-parchment-base p-4" data-testid={`tech-metric-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="font-display text-2xl font-bold text-forest-deep">{metric.value}</div>
                  <div className="mt-1 text-xs font-bold text-forest-deep/70">{metric.label}</div>
                  <div className="mt-1 text-[11px] text-forest-deep/50">{metric.note}</div>
                </motion.div>
              ))}
            </div>
            <div className="mt-5 overflow-hidden rounded-2xl border border-forest-deep/10">
              {tab.rows.map(([action, owner, status]) => (
                <div key={action} className="grid gap-2 border-b border-forest-deep/5 px-4 py-3 text-sm last:border-0 sm:grid-cols-[1.4fr_0.7fr_0.7fr] sm:items-center">
                  <span className="font-semibold text-forest-deep">{action}</span>
                  <span className="text-forest-deep/55">{owner}</span>
                  <span className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${statusTone(status)}`}><CheckCircle2 className="h-3.5 w-3.5" />{status}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-forest-deep/55">Scoped to the engagement. This is a product concept and does not represent a live client account.</p>
            {!compact && (
              <Link to="/contact?topic=Client%20technology%20%2F%20SaaS%20walkthrough" className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest-primary px-5 py-2.5 font-display text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-forest-rich" data-testid="tech-walkthrough-button">
                Arrange a product walkthrough <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
